/**
 * Pure sale-line quantity / cancel helpers.
 * Kept free of saleBalance imports so money readers can share these without cycles.
 */

import { getCartLineAmount } from './packSellMath';

/** Field order for unit price on sale lines / cart / receipt / analytics SSOT. */
export const SALE_LINE_UNIT_PRICE_KEYS = [
  'sellingUnitPrice',
  'sellingPrice',
  'price',
  'unitPrice',
  'salePrice'
];

/**
 * First finite unit price from known aliases (0 is valid).
 * @param {object|null|undefined} product
 * @returns {number}
 */
export function getSaleLineUnitPrice(product) {
  if (!product || typeof product !== 'object') return 0;
  for (let i = 0; i < SALE_LINE_UNIT_PRICE_KEYS.length; i += 1) {
    const raw = product[SALE_LINE_UNIT_PRICE_KEYS[i]];
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

export function getSaleLineActiveQuantity(product) {
  const qty = Math.max(0, Number(product?.quantity) || 0);
  const voided = Math.max(0, Number(product?.voidedQuantity) || 0);
  return Math.max(0, qty - voided);
}

export function getSaleLineActiveAmount(product) {
  // Exact carton money (e.g. 110 for 12 bottles) when pack fields present
  if (
    product?.sellMode === 'pack' &&
    Number(product.sellingPackPrice) > 0 &&
    Number(product.unitsPerPack) > 1
  ) {
    const activeQty = getSaleLineActiveQuantity(product);
    return getCartLineAmount({
      ...product,
      quantity: activeQty,
      price: getSaleLineUnitPrice(product)
    });
  }
  return getSaleLineActiveQuantity(product) * getSaleLineUnitPrice(product);
}

export function isSaleFullyCancelled(sale) {
  const status = String(sale?.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'voided' || sale?.voided === true) return true;
  const products = Array.isArray(sale?.products) ? sale.products : [];
  if (products.length === 0) return status === 'cancelled';
  return products.every((p) => getSaleLineActiveQuantity(p) <= 0);
}

/**
 * Void-aware active sale total (line SSOT when products exist).
 * @param {object|null|undefined} sale
 * @returns {number}
 */
export function computeSaleActiveTotal(sale) {
  if (isSaleFullyCancelled(sale)) return 0;
  const products = Array.isArray(sale?.products) ? sale.products : [];
  if (products.length === 0) {
    return Number(sale?.totalAmount ?? sale?.total ?? 0) || 0;
  }
  return products.reduce((sum, p) => sum + getSaleLineActiveAmount(p), 0);
}

/**
 * Persistable money fields after void / cancel — total always matches active lines.
 * @param {object} sale
 * @param {{ forceZero?: boolean }} [opts]
 * @returns {{ total: number, totalAmount: number }}
 */
export function resolvePersistedSaleTotals(sale, opts = {}) {
  if (opts.forceZero || isSaleFullyCancelled(sale)) {
    return { total: 0, totalAmount: 0 };
  }
  const active = Math.round(computeSaleActiveTotal(sale) * 100) / 100;
  return { total: active, totalAmount: active };
}
